import { useMemo } from "react";
import type { SourceType } from "@/hooks/firestore/betting/types";
import {
  isBettingEvent,
  type MarketType,
  type ProcessedOutcome,
  useEvent,
} from "@/hooks/firestore/betting/useEvent";
import { useHasPermission } from "@/features/auth/hooks/usePermission";
import { isLiveViewOnly } from "utilities/sharedBettingUtilities";
import type { BettingType, EventStatus } from "@/hooks/firestore/betting/types";
import type { MarketStatus } from "@/features/betslip/types";

export type StatusAndOddsOutcome = {
  canViewLiveBets: boolean;
  eventStatus: EventStatus | undefined;
  marketStatus: MarketStatus;
  /** The event's main market, which is what sets the event's own lifecycle.
   *  Event-scoped, so every outcome on the event reads the same value. */
  mainMarketStatus: MarketStatus | undefined;
  // Durable: a suspended live market loses its LIVE `marketStatus`.
  bettingType: BettingType;
  /** Live, and this user can't bet live - the one place holding the event,
   *  the market and the permission at once. No price, but still on screen. */
  isLiveViewOnly: boolean;
  streamUri: string;
} & ProcessedOutcome;

export type Outcome = {
  marketId: string;
  outcomeId: string;
};

type StatusAndOddsByOutcomeId = Record<string, StatusAndOddsOutcome>;

export const useOutcomeStatusAndOdds = (
  outcomeId: string,
  eventId: string,
  marketTypes: MarketType = "all",
  // Record lookup misses while the event is loading — surface that in the
  // type so consumers guard.
): {
  outcome: StatusAndOddsOutcome | undefined;
  isLoading: boolean;
  // Event-scoped, so a caller can still tell live from gone when the outcome's
  // market has dropped out of the feed.
  eventStatus: EventStatus | undefined;
  canViewLiveBets: boolean;
} => {
  const { outcomes, isLoading, eventStatus, canViewLiveBets } =
    useMultipleOutcomeStatusAndOdds(eventId, [outcomeId], marketTypes);

  return {
    outcome: outcomes[outcomeId],
    isLoading,
    eventStatus,
    canViewLiveBets,
  };
};

export const useMultipleOutcomeStatusAndOdds = (
  eventId: string,
  outcomeIds: string[],
  marketTypes: MarketType = "all",
) => {
  const canViewLiveBets = useHasPermission("viewLiveMarkets");
  const { event, isLoading } = useEvent(eventId, marketTypes);
  const markets = useMemo(() => {
    if (!event) return;

    return [
      ...event.markets,
      // merge the main market if there is one with the rest of the markets
      ...(isBettingEvent(event) && event.mainMarket ? [event.mainMarket] : []),
    ];
  }, [event]);

  const outcomeStatuses = useMemo(() => {
    if (!event || !markets) return {};

    return outcomeIds.reduce((statusesByOutcomeId, outcomeId) => {
      const market = markets.find((market) => !!market.outcomes?.[outcomeId]);

      if (!market) return statusesByOutcomeId;

      const outcome = market.outcomes?.[outcomeId] ?? {};
      const eventStatus = event?.status ?? market?.status;

      statusesByOutcomeId[outcomeId] = {
        ...outcome,
        canViewLiveBets,
        eventStatus,
        marketStatus: market?.status,
        mainMarketStatus: isBettingEvent(event)
          ? event.mainMarket?.status
          : undefined,
        bettingType: market.bettingType,
        isLiveViewOnly: isLiveViewOnly(
          canViewLiveBets,
          eventStatus,
          market.bettingType,
        ),
        streamUri:
          isBettingEvent(event) && event.streamUri ? event?.streamUri : "",
      };

      return statusesByOutcomeId;
    }, {} as StatusAndOddsByOutcomeId);
  }, [outcomeIds, markets, event, canViewLiveBets]);

  return {
    outcomes: outcomeStatuses,
    // Event-scoped, so it survives when the outcome's market drops out of the feed.
    eventStatus: event?.status,
    canViewLiveBets,
    isLoading,
  } as const;
};
